import { cloneDeep, range } from "lodash";
import { connect } from "react-redux";
import { Form, getIn } from "formik";
import moment from "moment-timezone";
import React, { Component } from "react";
import ReactSelect, { components as SelectComponents } from "react-select";
import { withRouter } from "react-router-dom";

import { allottedTimeFactory } from "@/components/Provider/ScheduleAvailability/constants";
import { awaitNotesSummaryRefresh } from "@/features/notes/helpers/awaitNotesSummaryRefresh";
import Button from "@/components/UI/Button";
import CardSecondary from "@/components/UI/Card/CardSecondary";
import { CustomForm } from "@/component-library";
import { DashboardCard2 } from "@/components/layout/DashboardCard2";
import DatePickerWithDisplay from "@/components/UI/DatePickerWithDisplay";
import FormikInput from "@/components/UI/FormikInput";
import GenericQuestionaire from "@/containers/Dashboard/Patient/questionaires/GenericQuestionaire";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import images from "@/utils/images";
import InputError from "@/components/UI/InputError";
import Label from "@/components/UI/Label";
import { MemberHeaderHOC } from "@/component-library/hocs/MemberHeaderHOC";
import { patientRequests } from "@/api/(requests)/patient";
import { providerRequests } from "@/api/(requests)/provider";
import { SCREENING_OPTION_LABEL_MAP } from "@/constants/rating";
import { sharedRequests } from "@/api/(requests)/shared";
import { testLocator } from "@/lib/helpers/test-locator";

import {
  CARE_COMMUNICATION_INITIAL_VALUES,
  CARE_COMMUNICATION_SCHEMA,
  CARE_COMMUNICATION_VALIDATION,
} from "./CareCommunicationFormSchema";
import { ENCOUNTER_INITIAL_VALUES } from "./Encounter/EncounterSchema";
import EncounterForm from "./Encounter/EncounterForm";

import "./styles.scss";

class CareCommunicationForm extends Component {
  state = {
    icd10Axis1: null,
    icd10Axis2: null,
    initialValues: null,
    patientDiagnosis: null,
    patientId: this.props.match.params.patientId,
    patientName: "",
    questionList: null,
    schema: null,
  };

  async componentDidMount() {
    const icd10response = await providerRequests.fetch_ICD10_dataset();
    const newState = {
      icd10Axis1: icd10response.filter((icd) => icd.axis === "1"),
      icd10Axis2: icd10response.filter((icd) => icd.axis === "2"),
    };
    const urlParams = { patientId: this.props.match.params.patientId };

    newState["patientDiagnosis"] = await sharedRequests.get_patient_diagnosis({ urlParams });

    const patientDetails = await sharedRequests.fetch_patient_details({ urlParams });

    newState["patientName"] = patientDetails.user.first_name + " " + patientDetails.user.last_name;

    this.setState({
      questionList: {
        gad: (await patientRequests.fetch_GAD7_questions())["question"],
        phq: (await patientRequests.fetch_PHQ9_questions())["question"],
      },
    });

    const initialValues = cloneDeep(CARE_COMMUNICATION_INITIAL_VALUES);
    const providerType = this.props.profile.provider_type;

    initialValues.patient_id = this.state.patientId;
    initialValues.patient_first_name = patientDetails.user.first_name;
    initialValues.patient_last_name = patientDetails.user.last_name;
    initialValues.provider_id = this.props.profile.username;
    initialValues.provider_type = providerType;
    initialValues.provider_cp_credentials = this.props.profile.cp_credentials;
    initialValues.provider_name = this.props.profile.name;
    initialValues.session_date = moment().format("MM/DD/YYYY");
    initialValues.ICD_10_axis_1_diagnosis = newState["patientDiagnosis"].ICD_10_axis_1_diagnosis;
    initialValues.ICD_10_axis_2_diagnosis = newState["patientDiagnosis"].ICD_10_axis_2_diagnosis;
    initialValues.content.encounters = [];
    newState["initialValues"] = initialValues;

    const schema = cloneDeep(CARE_COMMUNICATION_SCHEMA);
    const cptCodesFormEl = schema.find((formEl) => formEl.name === "call_type");

    cptCodesFormEl.elementConfig.options = cptCodesFormEl.elementConfig.options.filter((option) =>
      option.provider_types.includes(providerType),
    );
    newState["schema"] = schema;
    this.setState(newState);
  }

  addEncounter = async (formikProps) => {
    const updatedEncounters = [...formikProps.values.content.encounters];

    updatedEncounters.push(cloneDeep(ENCOUNTER_INITIAL_VALUES));
    await formikProps.setFieldValue("content.encounters", updatedEncounters);
  };

  removeEncounter = async (formikProps, encounterIndex) => {
    const updatedEncounters = [...formikProps.values.content.encounters];

    updatedEncounters[encounterIndex].delete_marker = true;
    updatedEncounters[encounterIndex].encounter_type = ["none"];
    updatedEncounters[encounterIndex].setting = ["none"];
    await formikProps.setFieldValue("content.encounters", updatedEncounters);
  };

  signatureClickHandler = async (name, formikProps) => {
    if (!formikProps.values.signature) {
      await formikProps.setFieldValue(name, formikProps.values.provider_name);
    } else {
      await formikProps.setFieldValue(name, "");
    }
  };

  optionClickHandler = async (name, option, options, formikProps) => {
    const selectedOptions = [...options];

    if (selectedOptions.includes(option.value)) {
      selectedOptions.splice(selectedOptions.indexOf(option.value), 1);
      option.checked = false;
    } else {
      selectedOptions.push(option.value);
      option.checked = true;
    }
    await formikProps.setFieldValue(name, selectedOptions);
  };

  setFieldValue = async (name, value, formikProps) => {
    await formikProps.setFieldValue(name, value);
  };

  dateClickHandler = async (dateStr, _eventType, formEl, formikProps) => {
    const parts = dateStr.split("-");

    await formikProps.setFieldValue(formEl.name, parts[1] + "/" + parts[2] + "/" + parts[0]);
  };

  reasonForCommunicationClickHandler = (name, option, formikProps) => {
    const existing_reasons = formikProps.values.content.reason_for_communication;

    if (
      option.value === "Acute Care Encounter" &&
      !existing_reasons.includes(option.value) &&
      !formikProps.values.content.encounters.filter((encounter) => !encounter.delete_marker).length
    ) {
      this.addEncounter(formikProps);
    } else if (option.value === "Acute Care Encounter" && existing_reasons.includes(option.value)) {
      range(formikProps.values.content.encounters.length).forEach((encounterIndex) =>
        this.removeEncounter(formikProps, encounterIndex),
      );
    }
    this.optionClickHandler(name, option, existing_reasons, formikProps);
  };

  preventEnterFormSubmission = (e) => {
    if (e.key === "Enter" || e.code === "Enter") {
      e.preventDefault();
    }
  };

  renderForm = (formikProps) => {
    return (
      <>
        <Form onKeyDown={this.preventEnterFormSubmission}>
          <div className="row">
            {this.state.schema.map((formEl) => {
              if (formEl.name === "content.reason_for_communication") {
                formEl.onclick = (name, option) =>
                  this.reasonForCommunicationClickHandler(name, option, formikProps);
                const openTextOption = formEl.elementConfig.options.find((option) =>
                  Object.hasOwn(option, "openText"),
                );

                openTextOption.onChange = async (e) => {
                  const val = e.target.value;

                  await formikProps.setFieldValue("content.reason_for_communication_other", val);
                };
              }

              const error = getIn(formikProps.errors, formEl.name);
              const touched = getIn(formikProps.touched, formEl.name);

              if (formEl.elementType === "encounters") {
                const encounterSelected =
                  formikProps.values.content.reason_for_communication.includes(
                    "Acute Care Encounter",
                  );

                if (encounterSelected) {
                  return (
                    <>
                      <div key={formEl.name}>
                        {formikProps.values.content.encounters.map((_encounter, encounterIndex) => {
                          return (
                            <>
                              <EncounterForm
                                dateClickHandler={(dateStr, eventType, formEl) =>
                                  this.dateClickHandler(dateStr, eventType, formEl, formikProps)
                                }
                                encounterIndex={encounterIndex}
                                formikProps={formikProps}
                                optionClickHandler={this.optionClickHandler}
                                removeEncounter={this.removeEncounter}
                                setFieldValue={(name, value) =>
                                  this.setFieldValue(name, value, formikProps)
                                }
                              />
                            </>
                          );
                        })}
                        <div className="d-flex mb-2 tw:justify-center">
                          <Button
                            className="Btn Btn--pri Btn-sm-2"
                            onClick={() => this.addEncounter(formikProps)}
                            type="button"
                          >
                            Add Additional Encounter
                          </Button>
                        </div>
                      </div>
                      <div className="border-bottom col-12 my-2" />
                    </>
                  );
                } else {
                  return <></>;
                }
              } else if (formEl.name.includes("ICD_10_axis")) {
                return (
                  <div
                    className="col-12 col-lg-6 mb-3"
                    key={formEl.name}
                    {...(formEl.name === "ICD_10_axis_1_diagnosis"
                      ? testLocator("primaryDiagnosisSelect")
                      : {})}
                  >
                    <Label>{formEl.elementConfig.label}</Label>
                    <ReactSelect
                      className="basic-multi-select"
                      classNamePrefix="select"
                      components={{
                        ClearIndicator: null,
                        Option: ({ children, innerProps, ...props }) => (
                          <SelectComponents.Option
                            {...props}
                            innerProps={{ ...innerProps, ...testLocator("diagnosisOption") }}
                          >
                            {children}
                          </SelectComponents.Option>
                        ),
                      }}
                      defaultValue={formikProps.values[formEl.name]}
                      inputId={
                        formEl.name === "ICD_10_axis_1_diagnosis"
                          ? "diagnosisSelect1"
                          : "diagnosisSelect2"
                      }
                      isMulti
                      onChange={async (selectedOptions) =>
                        await formikProps.setFieldValue(formEl.name, selectedOptions)
                      }
                      options={
                        formEl.name === "ICD_10_axis_1_diagnosis"
                          ? this.state.icd10Axis1
                          : this.state.icd10Axis2
                      }
                    />
                    {error && touched ? (
                      <InputError classes="custom-error" testId="primaryDiagnosisError">
                        {error}
                      </InputError>
                    ) : null}
                  </div>
                );
              } else if (formEl.name === "signature") {
                formEl.onclick = (name) => this.signatureClickHandler(name, formikProps);

                return (
                  <div className={formEl.elementClasses} key={formEl.name}>
                    <FormikInput
                      errors={formikProps.errors}
                      formEl={formEl}
                      touched={formikProps.touched}
                    />
                    <div
                      className="w-100 fs-24 border-bottom"
                      style={{ fontFamily: "Apple Chancery, cursive" }}
                    >
                      {"X " + formikProps.values.signature}
                    </div>
                  </div>
                );
              } else if (formEl.elementType === "datePicker") {
                return (
                  <div className={formEl.elementClasses} key={formEl.name}>
                    <DatePickerWithDisplay
                      dateClickHandler={(dateStr, eventType, formEl) =>
                        this.dateClickHandler(dateStr, eventType, formEl, formikProps)
                      }
                      dateStr={formikProps.values[formEl.name]}
                      formEl={formEl}
                      formikProps={formikProps}
                      touched={() => formikProps.setFieldTouched(formEl.name, true)}
                    />
                  </div>
                );
              } else {
                return (
                  <div className={formEl.elementClasses} key={formEl.name}>
                    <FormikInput
                      disabled={formEl.disabled}
                      errorClasses={formEl.errorClasses}
                      errors={formikProps.errors}
                      formEl={formEl}
                      inputClasses={formEl.inputClasses}
                      touched={formikProps.touched}
                    />
                  </div>
                );
              }
            })}
          </div>
          <div className="d-flex justify-content-lg-end tw:justify-center">
            <Button className="Btn Btn--pri" type="submit" {...testLocator("saveNoteBtn")}>
              Save Note
            </Button>
          </div>
        </Form>
      </>
    );
  };

  submitHandler = async (formData) => {
    if (
      !formData.content.reason_for_communication.includes("Acute Care Encounter") &&
      formData.content.encounters
    ) {
      formData.content.encounters = [];
    } else {
      formData.content.encounters = formData.content.encounters.filter(
        (encounter) => !encounter.delete_marker,
      );
    }
    formData.session_from = moment(
      formData.session_from_hr +
        ":" +
        formData.session_from_min +
        " " +
        formData.session_from_am_pm,
      "HH:mm a",
    ).format("HH:mm");

    formData["session_to"] = moment(formData["session_from"], "HH:mm")
      .add(allottedTimeFactory(formData["call_type"]), "minutes")
      .format("HH:mm");

    formData.is_billable = formData.call_type !== "non_billable";

    const urlParams = { patientId: this.state.patientId };

    await providerRequests.post_progress_note({ data: formData, urlParams });
    await awaitNotesSummaryRefresh(this.state.patientId, this.props.dispatch);
    this.props.history.push(`/app/patient/${this.state.patientId}/profile/v2?selectedTab=Notes`);
  };

  renderGADPHQCards = (value) => {
    const { cardDescription, cardTitle, questionList } = value;

    if (!questionList) {
      return;
    }

    const openButtonRender = (
      <CardSecondary className="p-3 d-flex cursor" key={cardTitle}>
        <div className="col-11">
          <div className="row">
            <b>{cardTitle}</b>
          </div>
          <div className="row txt-gry">{cardDescription}</div>
        </div>
        <div className="col-1 d-flex">
          <img
            alt="note icon"
            className="new-note-chevron align-self-center"
            src={images("./common/right-chevron.svg")}
            style={{ maxHeight: "22px" }}
          />
        </div>
      </CardSecondary>
    );

    return (
      <GenericQuestionaire
        displayAsModal={true}
        labelMap={SCREENING_OPTION_LABEL_MAP}
        modalOpenButton={openButtonRender}
        {...value}
      />
    );
  };

  generateQuestionaireCards = () => [
    {
      cardDescription:
        "Complete the screening with the member, this records a billing event and saves the results to their progres trends",
      cardTitle: "Verbally Administer a Patient Health Questionnaire - 9 (PHQ-9)",
      questionaireInfoDescription:
        "Over the past 2 weeks, how often have you been bothered by any of the following problems?",
      questionaireInfoTitle: "Patient Health Questionnaire - 9",
      questionList: this.state.questionList.phq,
      submitFn: async (data) =>
        await patientRequests.submit_PHQ9_questions({
          data: {
            ...data,
            billable_data: {
              patient_name: this.state.patientName,
              provider_name: this.props.profile.name,
              provider_username: this.props.profile.username,
              screening_type: "phq",
            },
            patient_id: this.state.patientId,
          },
        }),
      title: "Clinician Supported PHQ-9",
    },
    {
      cardDescription:
        "Complete the screening with the member, this records a billing event and saves the results to their progres trends",
      cardTitle: "Verbally Administer a General Anxiety Disorder - 7 (GAD-7)",
      questionaireInfoDescription:
        "How often have they been bothered by the following over the past 2 weeks?",
      questionaireInfoTitle: "General Anxiety Disorder - 7",
      questionList: this.state.questionList.gad,
      submitFn: async (data) =>
        await patientRequests.submit_GAD7_questions({
          data: {
            ...data,
            billable_data: {
              patient_name: this.state.patientName,
              provider_name: this.props.profile.name,
              provider_username: this.props.profile.username,
              screening_type: "gad",
            },
            patient_id: this.state.patientId,
          },
        }),
      title: "Clinician Supported GAD-7",
    },
  ];

  render() {
    return (
      <>
        <section className="tw:flex tw:flex-col tw:items-center">
          <div className="survey-back-link-wpr mr-auto fs-16 mt-2 p-3 pl-lg-0 pr-lg-0">
            <span
              className="survey-back-link"
              onClick={() =>
                this.props.history.push(
                  `/app/patient/${this.state.patientId}/profile/v2?selectedTab=Notes`,
                )
              }
            >
              <img alt="<" className="img-fluid" src={images("./common/solidBckBtn.svg")} />
              <p className="d-inline ml-2">Back to Patient Profile</p>
            </span>
          </div>

          <MemberHeaderHOC memberId={this.state.patientId} />
        </section>

        <DashboardCard2
          className=""
          title="Care Communication Note"
          {...getAutomationDataAttr("CareCommunicationForm__careCommunicationTitle")}
        >
          {this.state.initialValues && (
            <CustomForm
              initialValues={this.state.initialValues}
              onSubmit={this.submitHandler}
              render={this.renderForm}
              validateOnBlur={true}
              validateOnChange={true}
              validationSchema={CARE_COMMUNICATION_VALIDATION}
            />
          )}
        </DashboardCard2>

        {this.state.questionList && this.generateQuestionaireCards().map(this.renderGADPHQCards)}
      </>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    profile: state.auth.profile,
  };
};

export default connect(mapStateToProps)(withRouter(CareCommunicationForm));
